import React, { useCallback, useState } from 'react'

import { Box } from '@mui/material'
import { useSelector } from 'react-redux'

import ItemRequestActionModal from '../../../../shared/components/ItemRequestActionModal/ItemRequestActionModal.component'
import ReadyToPurchaseTable from '../../../../shared/components/ReadyToPurchaseTable/ReadyToPurchaseTable.component'
import Tabs from '../../../../shared/components/Tabs/Tabs.component'

import {
  getPurchaseReadyItems,
  selectPurchaseReadyItemsForStatus
} from '../../../../redux/states/admin/getPurchaseReadyItems/getPurchaseReadyItems.slice'
import {
  ItemRequest,
  OfferRequestDetails
} from '../../../../redux/states/admin/getPurchaseReadyItems/getPurchaseReadyItems.types'
import { updateItemRequestTrackingDetails } from '../../../../redux/states/offers/updateItemRequestTrackingDetails/updateItemRequestTrackingDetails.slice'
import { UpdateItemRequestTrackingDetailsRequestPayload } from '../../../../redux/states/offers/updateItemRequestTrackingDetails/updateItemRequestTrackingDetails.types'
import { RootState } from '../../../../redux/store/store.types'
import {
  FilterStatus,
  apiStatusForTabIndex
} from '../../../../shared/components/ReadyToPurchaseTable/ReadyToPurchaseTable.dictionary'
import status from '../../../../shared/functions/Status/status.dictionaries'
import { useAppDispatch } from '../../../../shared/hooks/redux.hooks'
import styles from './ReadyToPurchase.styles'
import { ReadyToPurchaseProps } from './ReadyToPurchase.types'

const ReadyToPurchase: React.FC<ReadyToPurchaseProps> = (props: ReadyToPurchaseProps) => {
  const { setReadyToPurchaseTabIndex, readyToPurchaseTabIndex, searchQuery } = props

  const [selectedItemRequest, setSelectedItemRequest] = useState<
    | (ItemRequest & {
        offerRequestDetails: OfferRequestDetails
      })
    | null
  >(null)
  const [showActionModal, setShowActionModal] = useState(false)

  const dispatch = useAppDispatch()

  // The status of the tab currently on screen. The action modal belongs to that
  // tab, so it must watch that tab's fetch — not "any fetch anywhere", which used
  // to spin the button whenever a background tab refreshed.
  const activeTabApiStatus = apiStatusForTabIndex(readyToPurchaseTabIndex)

  const { loading: updateTrackingLoading } = useSelector((state: RootState) => state.updateItemRequestTrackingDetails)
  const { loading: getPurchaseReadyItemsLoading } = useSelector(selectPurchaseReadyItemsForStatus(activeTabApiStatus))

  const handleRowClick = useCallback((row: unknown, _index: number) => {
    const typedRow = row as ItemRequest & {
      offerRequestDetails: OfferRequestDetails
    }

    setSelectedItemRequest(typedRow)
    setShowActionModal(true)
  }, [])

  const handleBuy = useCallback(() => {
    setShowActionModal(false)
    setSelectedItemRequest(null)
  }, [])

  const handleGetPurchaseReadyItems = useCallback(
    (status: string) => {
      dispatch(
        getPurchaseReadyItems({
          request: {
            page: 1,
            size: 10,
            status: status,
            searchQuery: ''
          },
          onSuccess: () => {
            setShowActionModal(false)
            setSelectedItemRequest(null)
          }
        })
      )
    },
    [dispatch]
  )

  const handleConfirmItemBought = useCallback(() => {
    if (!selectedItemRequest) return

    const payload: UpdateItemRequestTrackingDetailsRequestPayload = {
      request: {
        itemRequestId: selectedItemRequest.id.toString(),
        status: status.deliveryStatus.purchasedByAdmin
      },
      onSuccess: () => {
        handleGetPurchaseReadyItems(status.itemRequestStatus.readyForPurchase)
      }
    }

    dispatch(updateItemRequestTrackingDetails(payload))
  }, [selectedItemRequest, dispatch, handleGetPurchaseReadyItems])

  const handleConfirmDelivery = useCallback(() => {
    if (!selectedItemRequest) return

    const payload: UpdateItemRequestTrackingDetailsRequestPayload = {
      request: {
        itemRequestId: selectedItemRequest.id.toString(),
        status: status.deliveryStatus.confirmedDeliveredToTravelerAddressByAdmin
      },
      onSuccess: () => {
        // Refresh the list the admin is actually looking at. The per-tab mapping
        // lives in the table's dictionary so this and the table itself cannot
        // disagree about which status a tab index means.
        handleGetPurchaseReadyItems(activeTabApiStatus)
      }
    }

    dispatch(updateItemRequestTrackingDetails(payload))
  }, [selectedItemRequest, dispatch, handleGetPurchaseReadyItems, activeTabApiStatus])

  const handleDismiss = useCallback(() => {
    setShowActionModal(false)
    setSelectedItemRequest(null)
  }, [])

  const handleTabChange = (newValue: number) => {
    setReadyToPurchaseTabIndex(newValue)
  }

  const tabs = [
    {
      label: 'Ready to Purchase',
      content: (
        <ReadyToPurchaseTable
          onRowClick={handleRowClick}
          filterStatus={FilterStatus.readyForPurchase}
          searchQuery={searchQuery}
        />
      )
    },
    {
      label: 'Purchased',
      content: (
        <ReadyToPurchaseTable
          onRowClick={handleRowClick}
          filterStatus={FilterStatus.purchased}
          searchQuery={searchQuery}
        />
      )
    },
    {
      label: 'Delivered to Traveler',
      content: (
        <ReadyToPurchaseTable
          onRowClick={handleRowClick}
          filterStatus={FilterStatus.deliveredToTravelerAddress}
          searchQuery={searchQuery}
        />
      )
    },
    {
      label: 'Delivered to Shopper',
      content: (
        <ReadyToPurchaseTable
          onRowClick={handleRowClick}
          filterStatus={FilterStatus.deliveredToShopper}
          searchQuery={searchQuery}
        />
      )
    }
  ]

  return (
    <Box sx={styles.container}>
      <Tabs tabs={tabs} value={readyToPurchaseTabIndex} onChange={handleTabChange} />

      {selectedItemRequest && (
        <ItemRequestActionModal
          open={showActionModal}
          onClose={handleDismiss}
          itemRequest={selectedItemRequest}
          onBuy={handleBuy}
          onConfirmItemBought={handleConfirmItemBought}
          onConfirmDelivery={handleConfirmDelivery}
          onDismiss={handleDismiss}
          // Refresh the tab the admin is on when a secondary action changes it.
          refreshTable={() => handleGetPurchaseReadyItems(activeTabApiStatus)}
          loading={false}
          confirmItemBoughtLoading={updateTrackingLoading || getPurchaseReadyItemsLoading}
        />
      )}
    </Box>
  )
}

export default ReadyToPurchase
